import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Building2,
  FlaskConical,
  UserRound,
  List,
  Search,
  User,
  Users,
} from 'lucide-react'
import {
  ETICHETE_FAZA,
  ETICHETE_TIP,
  TIPURI_CAMPANIE,
  type FazaActivitate,
  type TipCampanie,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { CarduriSelector, type CardSelector } from '../../components/ui/CarduriSelector'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import { TON } from '../../components/ui/TonuriB4L'
import { Avatar } from '../taskuri/CardFata'
import { FormularContainer } from '../../components/marketing/eveniment/FormularContainer'
import { ExportExcel } from '../../components/marketing/eveniment/ExportExcel'
import { PanouActivitate } from '../../components/marketing/eveniment/PanouActivitate'
import { CalendarActivitati } from '../../components/marketing/eveniment/CalendarActivitati'
import { CalendarEchipa } from '../../components/marketing/eveniment/CalendarEchipa'
import {
  BaraBuget,
  IconMostre,
  PatratTip,
  TON_FAZA,
  bani,
  candRelativ,
  cuFaza,
  lei,
  semnale,
  type ActivitateCuFaza,
} from '../../components/marketing/eveniment/activitate'
import {
  despre,
  eroare,
  perioada,
  type Campanie,
  type PartenerCampanie,
  type UtilizatorScurt,
} from '../../components/marketing/eveniment/comun'

type Raspuns = { campanii: Campanie[]; ascunseTerminate?: number }
type CheieCard = 'toate' | 'aprobat' | 'atentie' | 'desfasurare' | 'de_inchis'
type Ordine = 'data' | 'titlu' | 'buget'

const FAZE_DESCHISE: FazaActivitate[] = ['propunere', 'pregatire', 'desfasurare', 'de_inchis']
const FAZE_INCHISE: FazaActivitate[] = ['incheiata', 'anulata']

const FILTRU_CARD: Record<CheieCard, (a: ActivitateCuFaza) => boolean> = {
  toate: () => true,
  aprobat: (a) => (a.cereriDeAprobat ?? 0) > 0,
  atentie: (a) => semnale(a).length > 0,
  desfasurare: (a) => a.faza === 'desfasurare',
  de_inchis: (a) => a.faza === 'de_inchis',
}

/**
 * PR & Evenimente — lista activitatilor (redesign 15.10.2026, macheta aprobata de Madalin).
 *
 * Grupata pe cele 5 faze comune, cu ce cere atentie pe fiecare rand. Clic pe rand = rezumatul in
 * panoul din dreapta; titlul (sau „Deschide activitatea") = pagina. Plus Calendar.
 *
 * Implicit se incarca doar activitatile deschise, ca inainte: lista calculeaza costul si plata
 * fiecarui rand (cu EA), iar cele terminate sunt multe. Incheiatele si calendarul le aduc la
 * cerere.
 */
export function CampaniiEvenimentePage() {
  const queryClient = useQueryClient()
  const { confirma } = useConfirmare()
  const permissions = useAuthStore((s) => s.permissions)
  const eu = useAuthStore((s) => s.user?.id)
  const poateScrie = permissions.includes('marketing.events:write')
  const poateVedeaEchipa = permissions.includes('marketing.events:read')
  // Exportul il are cine vede modulul (Madalin, 30.10.2026), nu doar cine scrie.
  const poateExporta = permissions.includes('marketing.events:read')

  /*
    Activitatile unui partener: `?partener=5` (id-ul partenerului comun), din rezumat sau din lista (15.10.2026).
    In URL, ca linkul sa se poata trimite. Arata si activitatile incheiate: istoria cu partenerul
    e tocmai ce se cauta.
  */
  const [parametri, setParametri] = useSearchParams()
  const partener = parametri.get('partener')
  const [card, setCard] = useState<CheieCard>('toate')
  const [tip, setTip] = useState<'toate' | TipCampanie>('toate')
  const [aleMele, setAleMele] = useState(false)
  const [cuMostre, setCuMostre] = useState(false)
  const [cuPartener, setCuPartener] = useState(false)
  const [cuColaborator, setCuColaborator] = useState(false)
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  const [vedere, setVedere] = useState<'lista' | 'calendar' | 'echipa'>(() => {
    try {
      const v = localStorage.getItem('b4l.pr.vedere')
      return v === 'calendar' || (v === 'echipa' && poateVedeaEchipa) ? v : 'lista'
    } catch {
      return 'lista'
    }
  })
  const [ordine, setOrdine] = useState<Ordine>('data')
  const [inchiseDeschise, setInchiseDeschise] = useState(false)
  const [ales, setAles] = useState<number | null>(null)
  const [creareDeschisa, setCreareDeschisa] = useState(false)
  const [exportDeschis, setExportDeschis] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])
  useEffect(() => {
    try {
      localStorage.setItem('b4l.pr.vedere', vedere)
    } catch {
      /* fara memorie locala, vederea revine la lista */
    }
  }, [vedere])

  const cuToate = inchiseDeschise || vedere === 'calendar' || Boolean(partener)
  const deschise = useQuery<Raspuns>({
    queryKey: ['campanii', 'deschise', q],
    queryFn: async () =>
      (await api.get<Raspuns>('/campanii', { params: { deschise: 'true', ...(q && { q }) } })).data,
  })
  const toate = useQuery<Raspuns>({
    queryKey: ['campanii', 'toate', q],
    queryFn: async () => (await api.get<Raspuns>('/campanii', { params: q ? { q } : {} })).data,
    enabled: cuToate,
  })

  const utilizatori = useQuery<{ users: UtilizatorScurt[] }>({
    queryKey: ['settings-users-scurt'],
    queryFn: async () =>
      (await api.get<{ users: UtilizatorScurt[] }>('/settings/users/echipa')).data,
  })
  const numeOm = useMemo(() => {
    const m = new Map<number, string>()
    for (const u of utilizatori.data?.users ?? []) m.set(u.id, u.name || u.email)
    return m
  }, [utilizatori.data])

  const creeaza = useMutation({
    mutationFn: async (date: Record<string, unknown>) => (await api.post('/campanii', date)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campanii'] })
      toast.success('Activitate creată')
      setCreareDeschisa(false)
    },
    onError: eroare,
  })
  const sterge = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/campanii/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campanii'] })
      setAles(null)
      toast.success('Activitate ștearsă')
    },
    onError: eroare,
  })

  const sursa = cuToate && toate.data ? toate.data : deschise.data
  const activitati = useMemo(() => (sursa?.campanii ?? []).map((c) => cuFaza(c)), [sursa])
  const ascunse = deschise.data?.ascunseTerminate ?? 0

  // Filtrele de sub carduri: tip, ale mele, cu mostre. Cardurile numara peste ele.
  const filtrate = useMemo(
    () =>
      activitati.filter(
        (a) =>
          (tip === 'toate' || a.tip === tip) &&
          (!aleMele || a.responsabilId === eu) &&
          (!cuMostre || a.areMostre) &&
          (!cuPartener || a.cuPartener) &&
          (!cuColaborator || a.cuColaborator) &&
          (!partener || (a.parteneri ?? []).some((p) => despre(p).cheie === partener)),
      ),
    [activitati, tip, aleMele, cuMostre, cuPartener, cuColaborator, eu, partener],
  )
  const vazute = useMemo(() => filtrate.filter(FILTRU_CARD[card]), [filtrate, card])
  const sortate = useMemo(() => {
    const l = [...vazute]
    if (ordine === 'titlu') l.sort((a, b) => a.titlu.localeCompare(b.titlu, 'ro'))
    else if (ordine === 'buget') l.sort((a, b) => (bani(b).buget ?? -1) - (bani(a).buget ?? -1))
    else l.sort((a, b) => a.dataStart.localeCompare(b.dataStart))
    return l
  }, [vazute, ordine])

  const deschiseFiltrate = filtrate.filter((a) => FAZE_DESCHISE.includes(a.faza))
  const nr = (k: CheieCard) => deschiseFiltrate.filter(FILTRU_CARD[k]).length
  const carduri: CardSelector<CheieCard>[] = [
    {
      cheie: 'aprobat',
      eticheta: 'De aprobat',
      valoare: nr('aprobat'),
      detaliu: 'activități cu cereri de marfă',
      ton: nr('aprobat') ? 'rosu' : 'gri',
    },
    {
      cheie: 'atentie',
      eticheta: 'Cer atenție',
      valoare: nr('atentie'),
      detaliu: 'marfă, plată, închidere',
      ton: nr('atentie') ? 'galben' : 'gri',
    },
    {
      cheie: 'desfasurare',
      eticheta: 'În desfășurare',
      valoare: nr('desfasurare'),
      detaliu: 'rulează acum',
      ton: 'mov',
    },
    {
      cheie: 'de_inchis',
      eticheta: 'De închis',
      valoare: nr('de_inchis'),
      detaliu: 's-au terminat, neînchise',
      ton: nr('de_inchis') ? 'galben' : 'gri',
    },
  ]
  const tipuriPrezente = TIPURI_CAMPANIE.filter((t) => activitati.some((a) => a.tip === t))
  const aleasa = activitati.find((a) => a.id === ales) ?? null

  async function cereStergerea(a: ActivitateCuFaza) {
    if (
      await confirma({
        titlu: `Ștergi „${a.titlu}"?`,
        mesaj: 'Activitatea dispare cu tot ce e legat de ea. Nu se poate anula.',
        buton: 'Șterge',
        pericol: true,
      })
    )
      sterge.mutate(a.id)
  }

  const interogare = cuToate ? toate : deschise
  /*
    Fara drept pe modul, API-ul raspunde 403. Cardurile cu „0" ar spune ca nu e nimic de facut —
    nu e adevarat, doar ca omul nu are acces. Spunem asta, in romana.
  */
  const status = (deschise.error as { response?: { status?: number } } | null)?.response?.status
  if (status === 403)
    return (
      <div className="grid gap-4">
        <AntetPagina titlu="PR & Evenimente" />
        <p className="rounded-xl border border-b4l-border bg-b4l-surface p-6 text-center text-corp text-b4l-text-muted">
          N-ai acces la PR & Evenimente. Dacă ai nevoie, cere-l unui admin.
        </p>
      </div>
    )

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="PR & Evenimente"
        descriere="Evenimente, campanii, sponsorizări, barter cu influenceri, publicații. Clic pe un rând pentru rezumat."
        actiuni={
          <div className="flex flex-wrap gap-2">
            {poateExporta && (
              <button
                type="button"
                onClick={() => setExportDeschis(true)}
                className="rounded-caseta border border-b4l-border bg-b4l-surface px-3.5 py-2 text-corp font-semibold text-b4l-text hover:bg-b4l-bg-soft"
              >
                Exportă Excel
              </button>
            )}
            {poateScrie && (
              <button
                type="button"
                onClick={() => setCreareDeschisa(true)}
                className="rounded-caseta bg-b4l-blue px-3.5 py-2 text-corp font-semibold text-white hover:opacity-90"
              >
                + Activitate nouă
              </button>
            )}
          </div>
        }
      />

      {/* Vederea „Echipa” are filtrele ei: cardurile, tipurile si cautarea listei nu i se aplica. */}
      {vedere !== 'echipa' && (
        <CarduriSelector
          carduri={carduri}
          ales={card}
          onAlege={(k) => setCard(card === k ? 'toate' : k)}
          eticheta="Ce cere atenție"
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className={`flex flex-wrap items-center gap-1.5 ${vedere === 'echipa' ? 'hidden' : ''}`}
        >
          <ChipuriFiltru
            eticheta="Tip"
            ales={tip}
            onAlege={setTip}
            chipuri={[
              { cheie: 'toate' as const, eticheta: 'Toate', numar: activitati.length },
              ...tipuriPrezente.map((t) => ({
                cheie: t,
                eticheta: ETICHETE_TIP[t],
                numar: activitati.filter((a) => a.tip === t).length,
              })),
            ]}
          />
          <Comutator activ={aleMele} onClick={() => setAleMele(!aleMele)}>
            <User size={13} /> Ale mele
          </Comutator>
          <Comutator activ={cuMostre} onClick={() => setCuMostre(!cuMostre)}>
            <FlaskConical size={13} /> Cu mostre
          </Comutator>
          <Comutator activ={cuPartener} onClick={() => setCuPartener(!cuPartener)}>
            <Building2 size={13} /> Cu partener
          </Comutator>
          <Comutator activ={cuColaborator} onClick={() => setCuColaborator(!cuColaborator)}>
            <UserRound size={13} /> Cu colaborator
          </Comutator>
          {partener && (
            <button
              type="button"
              onClick={() => {
                const p = new URLSearchParams(parametri)
                p.delete('partener')
                setParametri(p, { replace: true })
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-b4l-blue-light px-3 py-1 text-mic font-semibold text-b4l-blue-dark"
              aria-label="Scoate filtrul de partener"
            >
              Partener: {numePartener(activitati, partener) ?? 'ales'} <span aria-hidden>×</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            className={`flex w-full items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-b4l-text-dim sm:w-64 ${vedere === 'echipa' ? 'hidden' : ''}`}
          >
            <Search size={14} />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Caută titlu, cod, acțiune…"
              aria-label="Caută activități"
              className="w-full bg-transparent text-corp text-b4l-text outline-none"
            />
          </label>
          <div
            role="group"
            aria-label="Vedere"
            className="inline-flex rounded-caseta bg-b4l-bg p-0.5"
          >
            {(
              [
                ['lista', 'Listă', List],
                ['calendar', 'Calendar', CalendarDays],
                ['echipa', 'Echipa', Users],
              ] as const
            )
              // Calendarul echipei cere PR & Evenimente; cine citeste doar prin Sampling nu-l are.
              .filter(([k]) => k !== 'echipa' || poateVedeaEchipa)
              .map(([k, t, I]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={vedere === k}
                  onClick={() => setVedere(k)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-mic ${
                    vedere === k
                      ? 'bg-b4l-surface font-semibold text-b4l-text shadow-sm'
                      : 'text-b4l-text-muted'
                  }`}
                >
                  <I size={14} /> {t}
                </button>
              ))}
          </div>
        </div>
      </div>

      {card !== 'toate' && vedere !== 'echipa' && (
        <p className="text-mic text-b4l-text-muted">
          Filtrat: <b>{carduri.find((c) => c.cheie === card)?.eticheta}</b> ·{' '}
          <button
            type="button"
            onClick={() => setCard('toate')}
            className="text-b4l-blue-dark hover:underline"
          >
            arată toate
          </button>
        </p>
      )}

      <section className="rounded-xl border border-b4l-border bg-b4l-surface">
        {vedere === 'echipa' ? (
          // In afara `StareLista`: o cautare fara rezultat sau o lista goala nu ascunde echipa.
          <CalendarEchipa />
        ) : (
          <StareLista
            interogare={interogare}
            gol={!interogare.isLoading && activitati.length === 0}
            mesajGol={
              q
                ? `Nicio activitate pentru „${q}”.`
                : poateScrie
                  ? 'Nicio activitate deschisă. Apasă „+ Activitate nouă".'
                  : 'Nicio activitate deschisă.'
            }
          >
            {vedere === 'calendar' ? (
              <CalendarActivitati activitati={vazute} onAlege={setAles} />
            ) : (
              <div role="table" aria-label="Activități">
                <CapTabel ordine={ordine} onOrdine={setOrdine} />
                {sortate.length === 0 && (
                  <p className="px-4 py-8 text-center text-mic text-b4l-text-muted">
                    Nicio activitate pentru filtrele alese.
                  </p>
                )}
                {FAZE_DESCHISE.map((f) => (
                  <Grupa
                    key={f}
                    faza={f}
                    activitati={sortate.filter((a) => a.faza === f)}
                    ales={ales}
                    onAlege={setAles}
                    numeOm={numeOm}
                  />
                ))}
                <div className="border-t border-b4l-border">
                  <button
                    type="button"
                    onClick={() => setInchiseDeschise(!inchiseDeschise)}
                    aria-expanded={inchiseDeschise}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-mic text-b4l-text-muted hover:bg-b4l-bg-soft"
                  >
                    {inchiseDeschise ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {inchiseDeschise
                      ? 'Ascunde încheiatele și anulatele'
                      : `Arată încheiatele și anulatele${ascunse ? ` (${ascunse})` : ''}`}
                    {inchiseDeschise && toate.isFetching && (
                      <span className="text-b4l-text-dim">· se încarcă…</span>
                    )}
                  </button>
                  {inchiseDeschise &&
                    FAZE_INCHISE.map((f) => (
                      <Grupa
                        key={f}
                        faza={f}
                        activitati={sortate.filter((a) => a.faza === f)}
                        ales={ales}
                        onAlege={setAles}
                        numeOm={numeOm}
                      />
                    ))}
                </div>
              </div>
            )}
          </StareLista>
        )}
      </section>

      <PanouActivitate
        a={aleasa}
        numeResponsabil={aleasa?.responsabilId ? (numeOm.get(aleasa.responsabilId) ?? null) : null}
        onInchide={() => setAles(null)}
        onSterge={
          aleasa &&
          poateScrie &&
          !aleasa.generatAutomat &&
          (aleasa._count?.actiuniSampling ?? 0) === 0
            ? () => cereStergerea(aleasa)
            : undefined
        }
      />

      {exportDeschis && <ExportExcel onInchide={() => setExportDeschis(false)} />}

      {creareDeschisa && (
        <FormularContainer
          titlu="Activitate nouă"
          etichetaSalvare="Creează"
          utilizatori={utilizatori.data?.users ?? []}
          onClose={() => setCreareDeschisa(false)}
          onSubmit={(d) => creeaza.mutate(d)}
          seSalveaza={creeaza.isPending}
        />
      )}
    </div>
  )
}

/** Un filtru da/nu, in acelasi stil cu chip-urile. */
function Comutator({
  activ,
  onClick,
  children,
}: {
  activ: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={activ}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] transition ${
        activ
          ? 'border-b4l-text bg-b4l-text text-b4l-surface'
          : 'border-b4l-border bg-b4l-surface hover:bg-b4l-bg-soft'
      }`}
    >
      {children}
    </button>
  )
}

/*
  Coloanele, scrise intregi (Tailwind nu vede clasele compuse la rulare). Pe ecran mic randul se
  aseaza pe doua coloane: iconita, apoi totul sub titlu.
*/
const COLOANE_LG =
  'lg:grid-cols-[34px_minmax(0,2.4fr)_52px_minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_24px] lg:gap-3.5'

function CapTabel({ ordine, onOrdine }: { ordine: Ordine; onOrdine: (o: Ordine) => void }) {
  const Cap = ({ o, children }: { o: Ordine; children: string }) => (
    <button
      type="button"
      onClick={() => onOrdine(o)}
      aria-sort={ordine === o ? 'ascending' : undefined}
      className={`inline-flex items-center gap-1 uppercase tracking-wider ${ordine === o ? 'text-b4l-text' : ''}`}
    >
      {children}
      {ordine === o && <ChevronDown size={12} />}
    </button>
  )
  return (
    <div
      role="row"
      className={`hidden px-4 pb-1 pt-3 text-[11px] font-semibold text-b4l-text-dim lg:grid ${COLOANE_LG}`}
    >
      <span />
      <Cap o="titlu">Activitatea</Cap>
      <span className="uppercase tracking-wider" title="Are mostre">
        Mostre
      </span>
      <Cap o="data">Când</Cap>
      <span className="uppercase tracking-wider">Ce cere atenție</span>
      <Cap o="buget">Buget folosit</Cap>
      <span />
    </div>
  )
}

function Grupa({
  faza,
  activitati,
  ales,
  onAlege,
  numeOm,
}: {
  faza: FazaActivitate
  activitati: ActivitateCuFaza[]
  ales: number | null
  onAlege: (id: number) => void
  numeOm: Map<number, string>
}) {
  if (!activitati.length) return null
  const buget = activitati.reduce((s, a) => s + (bani(a).buget ?? 0), 0)
  return (
    <div role="rowgroup" className="border-t border-b4l-border first:border-t-0">
      <div className="sticky top-0 z-[1] flex items-center gap-2.5 bg-b4l-surface px-4 py-2">
        <Pastila ton={TON_FAZA[faza]}>{ETICHETE_FAZA[faza]}</Pastila>
        <span className="text-mic tabular-nums text-b4l-text-dim">
          {activitati.length} {activitati.length === 1 ? 'activitate' : 'activități'}
          {buget > 0 && ` · ${lei(buget)} buget`}
        </span>
      </div>
      {activitati.map((a) => (
        <Rand
          key={a.id}
          a={a}
          ales={ales === a.id}
          onAlege={() => onAlege(a.id)}
          responsabil={a.responsabilId ? (numeOm.get(a.responsabilId) ?? null) : null}
        />
      ))}
    </div>
  )
}

function Rand({
  a,
  ales,
  onAlege,
  responsabil,
}: {
  a: ActivitateCuFaza
  ales: boolean
  onAlege: () => void
  responsabil: string | null
}) {
  const s = semnale(a)
  const b = bani(a)
  const tasta = (e: KeyboardEvent) => {
    // Doar pe rand. Enter pe titlu (link) trebuie sa deschida pagina, nu panoul.
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onAlege()
    }
  }
  return (
    <div
      role="row"
      tabIndex={0}
      onClick={onAlege}
      onKeyDown={tasta}
      aria-label={`${a.titlu}, ${ETICHETE_FAZA[a.faza]}`}
      className={`grid cursor-pointer grid-cols-[34px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-1.5 px-4 py-2.5 outline-none hover:bg-b4l-bg-soft focus-visible:bg-b4l-bg-soft ${COLOANE_LG} ${
        ales ? 'bg-b4l-blue-light' : ''
      }`}
    >
      <PatratTip tip={a.tip} eticheta={ETICHETE_TIP[a.tip]} />
      <span className="grid min-w-0 gap-px">
        {/*
          Linkul cat textul, nu cat coloana. Intr-o grila, copilul se intinde pe toata latimea:
          un clic in spatiul alb de langa nume deschidea pagina in loc de rezumat (Madalin,
          15.10.2026). `justify-self-start` il tine cat numele; restul randului deschide panoul.
        */}
        <Link
          to={`/marketing/evenimente/${a.id}`}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full justify-self-start truncate font-medium text-b4l-text hover:underline"
        >
          {a.titlu}
        </Link>
        <span className="truncate text-mic text-b4l-text-dim">
          {ETICHETE_TIP[a.tip]}
          {a.oras && ` · ${a.oras}`} · <span className="font-mono text-[11px]">{a.cod}</span>
          {a.generatAutomat && ' · automat'}
          {a.parteneri && a.parteneri.length > 0 && ` · ${numeParteneri(a.parteneri)}`}
        </span>
      </span>
      <span className="max-lg:col-start-2">
        <IconMostre are={!!a.areMostre} />
      </span>
      <span className="grid gap-px text-mic max-lg:col-start-2">
        <span className="font-medium">{perioada(a)}</span>
        <span className="text-[11.5px] text-b4l-text-dim">{candRelativ(a)}</span>
      </span>
      <span className="flex flex-wrap gap-1 max-lg:col-start-2">
        {s.length ? (
          s.map((x) => (
            <span
              key={x.text}
              title={x.detaliu}
              className={`whitespace-nowrap rounded-full px-2 py-px text-[11.5px] font-semibold ${TON[x.ton].pastila}`}
            >
              {x.text}
            </span>
          ))
        ) : (
          <span className="text-mic text-b4l-text-dim">—</span>
        )}
      </span>
      <span className="grid gap-1 text-mic max-lg:col-start-2">
        {b.buget ? (
          <>
            <span className="flex justify-between gap-2 text-b4l-text-muted tabular-nums">
              <span>{lei(b.buget)}</span>
              <span className={b.folosit! > 100 ? 'font-semibold text-b4l-red-dark' : ''}>
                {b.folosit}%
              </span>
            </span>
            <BaraBuget a={a} />
          </>
        ) : (
          <span className="text-b4l-text-dim">
            {b.cheltuit ? `${lei(b.cheltuit)} · fără buget` : 'fără buget'}
          </span>
        )}
      </span>
      <span
        className="max-lg:hidden"
        title={responsabil ? `Responsabil: ${responsabil}` : 'Fără responsabil'}
      >
        <Avatar nume={responsabil} mic={false} />
      </span>
    </div>
  )
}

/** Primii doi parteneri, apoi câți mai sunt — pe rândul din listă. */
function numeParteneri(l: PartenerCampanie[]): string {
  const nume = [...new Set(l.map((p) => despre(p).nume))]
  return nume.length <= 2 ? nume.join(', ') : `${nume.slice(0, 2).join(', ')} +${nume.length - 2}`
}

function numePartener(
  activitati: { parteneri?: PartenerCampanie[] }[],
  cheie: string,
): string | null {
  for (const a of activitati)
    for (const p of a.parteneri ?? []) if (despre(p).cheie === cheie) return despre(p).nume
  return null
}
